import { TFunction } from 'i18next';

import { SPECIAL_CHAR_REGEX, UPPERCASE_REGEX } from 'constants/regex';
import { MINIMAL_PASSWORD_LENGTH } from 'constants/uiSettings';

const ENGLISH_CHARACTERS_REGEX = /^[A-Za-z'\s]*$/;
export const SALARY_CHARACTERS_REGEX = /^[0-9$€£¥₹\-,.\s]*$/;
export const CLIENT_NAME_REGEX = /^[A-Za-z0-9 '.,-]+$/;

const validateAllowedRegExp = (
  value: string,
  regex: RegExp,
  errorMessage: string
): Promise<void> => {
  if (value && !regex.test(value)) {
    return Promise.reject(new Error(errorMessage));
  }
  return Promise.resolve();
};

const validateAllowedCharacters = (
  value: string,
  allowedPattern: RegExp,
  errorMessage: string
): Promise<void> => {
  if (!value) {
    return Promise.resolve();
  }

  if (allowedPattern.test(value)) {
    return Promise.resolve();
  }

  const invalidChars = value
    .split('')
    .filter((char) => !allowedPattern.test(char))
    .join(', ');

  return Promise.reject(new Error(`${errorMessage}: ${invalidChars}`));
};

const validateEnglishCharacters = (_: unknown, value: string): Promise<void> =>
  validateAllowedRegExp(
    value,
    ENGLISH_CHARACTERS_REGEX,
    'Please use the English letters only'
  );

const validateSalaryCharacters = (_: unknown, value: string): Promise<void> =>
  validateAllowedRegExp(
    value,
    SALARY_CHARACTERS_REGEX,
    'Not allowed characters'
  );

const validateClientName = (_: unknown, value: string): Promise<void> =>
  validateAllowedCharacters(
    value,
    CLIENT_NAME_REGEX,
    'Client name contains invalid characters'
  );

const validateCompanyName = (_: unknown, value: string): Promise<void> =>
  validateAllowedCharacters(
    value,
    CLIENT_NAME_REGEX,
    'Company name contains invalid characters'
  );

const createPasswordValidationRule = (t: TFunction) => ({
  validator: (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(t('form.password.validation.create'));
    }
    if (value.length < MINIMAL_PASSWORD_LENGTH) {
      return Promise.reject(
        t('form.password.validation.newPasswordLength', {
          amount: MINIMAL_PASSWORD_LENGTH - value.length
        })
      );
    }
    if (!UPPERCASE_REGEX.test(value)) {
      return Promise.reject(t('form.password.validation.uppercase'));
    }
    if (!SPECIAL_CHAR_REGEX.test(value)) {
      return Promise.reject(t('form.password.validation.special'));
    }
    return Promise.resolve();
  }
});

export {
  createPasswordValidationRule,
  validateEnglishCharacters,
  validateSalaryCharacters,
  validateClientName,
  validateCompanyName
};
